import { logger } from '@heidi/runtime/logger';
import type {
  AgentEvent,
  FrontendEventAgentEvent,
  RunTitleGeneratedAgentEvent,
} from '../../../contracts';

export type BusEventHandler = (payload: {
  event_type: string;
  data: Record<string, unknown>;
}) => void;

export interface AgentEventDispatchContext {
  bus?: Record<string, BusEventHandler | undefined>;
  ui?: {
    'ui.toast'?: (payload: FrontendEventAgentEvent['payload']) => void;
    'ui.agent_demo'?: (payload: FrontendEventAgentEvent['payload']) => void;
    'ui.modal'?: (payload: FrontendEventAgentEvent['payload']) => void;
  };
  runTitleGenerated?: (event: RunTitleGeneratedAgentEvent) => void;
}

const isFrontendEvent = (event: AgentEvent): event is FrontendEventAgentEvent =>
  event.type === 'frontend.event' &&
  event.payload !== undefined &&
  typeof event.payload === 'object' &&
  event.payload !== null &&
  'event_type' in event.payload;

const isRunTitleGenerated = (event: AgentEvent): event is RunTitleGeneratedAgentEvent =>
  event.type === 'run.title_generated' &&
  event.payload !== undefined &&
  typeof event.payload === 'object' &&
  event.payload !== null &&
  'title' in event.payload;

export const dispatchAgentEvent = (
  event: AgentEvent,
  ctx: AgentEventDispatchContext
): void => {
  switch (event.type) {
    case 'channel.ended':
      return;
    case 'run.title_generated':
      if (isRunTitleGenerated(event)) ctx.runTitleGenerated?.(event);
      return;
    // Handled inline in useAgentRunChat's onAgentEvent before this dispatcher runs
    // (needs turn_id + the harness's own loader-phrase state, not a ctx handler).
    case 'turn.loader_phrases':
      return;
    case 'remote.tool_request':
    case 'tool.approval_requested':
    case 'sub_agent.run_started':
      return;
    case 'frontend.event':
      if (!isFrontendEvent(event)) return;
      if (event.payload.event_type === 'ui.toast') {
        dispatchRegisteredEvent(
          ctx.ui?.['ui.toast'],
          event.payload,
          event.payload.event_type
        );
        return;
      }
      if (event.payload.event_type === 'ui.agent_demo') {
        dispatchRegisteredEvent(
          ctx.ui?.['ui.agent_demo'],
          event.payload,
          event.payload.event_type
        );
        return;
      }
      if (event.payload.event_type === 'ui.modal') {
        dispatchRegisteredEvent(
          ctx.ui?.['ui.modal'],
          event.payload,
          event.payload.event_type
        );
        return;
      }
      dispatchRegisteredEvent(
        ctx.bus?.[event.payload.event_type],
        event.payload,
        event.payload.event_type
      );
      return;
    default:
      logger.info('[agent-harness] Unhandled agent event type', {
        type: event.type,
      });
      return;
  }
};

const dispatchRegisteredEvent = <T>(
  handler: ((payload: T) => void) | undefined,
  payload: T,
  eventType: string
) => {
  if (handler) {
    handler(payload);
    return;
  }
  logger.info('[agent-harness] Unhandled frontend event type', {
    event_type: eventType,
  });
};
